import { Pipe, PipeTransform } from '@angular/core';
import { LocaleService } from '@maximizer/core/shared/data-access';
import {
  DateTimeField,
  Field,
  FieldTypes,
  NumberField,
} from '@maximizer/core/shared/domain';
import { FormatSettings } from '@progress/kendo-angular-dateinputs';
import { NumberFormatOptions } from '@progress/kendo-angular-intl';

/** https://www.telerik.com/kendo-angular-ui/components/dateinputs/dateinput/formats */
/** Wide month and day: `MMMM d` — used for ignoreYear display */
const DATE_FORMAT_MONTH_DAY_WIDE = 'M';
/** Abbreviated month and day: `MMM d` — used for ignoreYear input */
const DATE_FORMAT_MONTH_DAY_ABBR = 'm';
/** Long date: `EEEE, MMMM d, y` — used for date display fallback */
const DATE_FORMAT_LONG = 'D';
/** Short date: `M/d/y` — used for date input */
const DATE_FORMAT_SHORT = 'd';
/** General date + short time: `M/d/y h:mm a` — used for datetime */
const DATE_FORMAT_GENERAL_SHORT_TIME = 'g';
/** Short time: `h:mm a` — used for time fallback */
const TIME_FORMAT_SHORT = 't';

@Pipe({
  name: 'numberFieldFormat',
  standalone: true,
})
export class NumberFieldFormatPipe implements PipeTransform {
  transform(field: Field): string | NumberFormatOptions {
    if (field.type === FieldTypes.Number) {
      const numberField = field as NumberField;
      if (numberField.currency) {
        return {
          style: 'currency',
          currency: numberField.currency,
          currencyDisplay: numberField.readonly ? 'code' : 'symbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: numberField.decimals ?? 0,
        };
      } else {
        return numberField.decimals ? 'n' + numberField.decimals : 'n0';
      }
    }
    return '';
  }
}

@Pipe({
  name: 'dateFieldFormat',
  standalone: true,
})
export class DateFieldFormatPipe implements PipeTransform {
  constructor(public locale: LocaleService) {}

  transform(field: Field): FormatSettings | string {
    if (field.type !== FieldTypes.DateTime) {
      return '';
    }

    const dateField = field as DateTimeField;

    switch (dateField.selection) {
      case 'time':
        return this.getTimeFormat(dateField);

      case 'date': {
        return this.getDateFormat(dateField);
      }

      case 'dateTime':
      default:
        return this.getDateTimeFormat(dateField);
    }
  }

  private getTimeFormat(dateField: DateTimeField): string {
    return (
      dateField.displayFormat ??
      this.locale.data.calendar?.timeFormats?.short ??
      TIME_FORMAT_SHORT
    );
  }

  private getDateTimeFormat(dateField: DateTimeField): FormatSettings {
    const defaultDisplayFormat = dateField.ignoreYear
      ? DATE_FORMAT_MONTH_DAY_ABBR
      : DATE_FORMAT_GENERAL_SHORT_TIME;

    const defaultInputFormat = dateField.ignoreYear
      ? DATE_FORMAT_MONTH_DAY_ABBR
      : DATE_FORMAT_GENERAL_SHORT_TIME;

    const displayFormat = dateField.displayFormat ?? defaultDisplayFormat;

    const inputFormat = dateField.readonly
      ? displayFormat
      : (dateField.inputFormat ?? defaultInputFormat);

    return { displayFormat, inputFormat };
  }

  private getDateFormat(dateField: DateTimeField): FormatSettings {
    const defaultDisplayFormat = dateField.ignoreYear
      ? DATE_FORMAT_MONTH_DAY_WIDE
      : (this.locale.data.calendar?.dateFormats?.long ?? DATE_FORMAT_LONG);

    const defaultInputFormat = dateField.ignoreYear
      ? DATE_FORMAT_MONTH_DAY_ABBR
      : DATE_FORMAT_SHORT;

    const displayFormat = dateField.displayFormat ?? defaultDisplayFormat;

    const inputFormat = dateField.readonly
      ? displayFormat
      : (dateField.inputFormat ?? defaultInputFormat);

    return { displayFormat, inputFormat };
  }
}
